import React, { useState, useEffect } from 'react';
import { useWeb3 } from '../../contexts/Web3Context';
import { MusicWorkNFT } from '../../types/blockchain';
import { 
  MusicalNoteIcon,
  UsersIcon,
  CurrencyDollarIcon,
  ClockIcon,
  ChartBarIcon,
  ArrowRightIcon,
  PlayIcon,
  PauseIcon,
  EyeIcon,
  ShareIcon,
  HeartIcon
} from '@heroicons/react/24/outline';
import { HeartIcon as HeartSolidIcon } from '@heroicons/react/24/solid';

const MusicWorkNFTDisplay: React.FC = () => {
  const { wallet } = useWeb3();
  const [musicWorks, setMusicWorks] = useState<MusicWorkNFT[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedWork, setSelectedWork] = useState<MusicWorkNFT | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'participants' | 'revenue' | 'transactions'>('overview');
  const [favorites, setFavorites] = useState<Set<string>>(new Set());
  const [playingWork, setPlayingWork] = useState<string | null>(null);

  useEffect(() => {
    loadMusicWorks();
  }, [wallet]);

  const loadMusicWorks = async () => {
    setLoading(true);
    try {
      const { UnifiedMockData } = await import('../../data/mock');
      const mockData = new UnifiedMockData();
      const works = mockData.getMusicWorkNFTs(wallet?.address);
      setMusicWorks(works);
    } catch (error) {
      console.error('Failed to load music works:', error);
    } finally {
      setLoading(false);
    }
  };

  const getRoleDisplayName = (role: string) => {
    const roleMap: { [key: string]: string } = {
      'composer': '作曲',
      'lyricist': '作词',
      'producer': '制作人',
      'vocalist': '主唱',
      'instrumentalist': '乐器演奏'
    };
    return roleMap[role] || role;
  };

  const getRoleColor = (role: string) => {
    const colorMap: { [key: string]: string } = {
      'composer': 'bg-blue-100 text-blue-800',
      'lyricist': 'bg-green-100 text-green-800',
      'producer': 'bg-purple-100 text-purple-800',
      'vocalist': 'bg-pink-100 text-pink-800',
      'instrumentalist': 'bg-orange-100 text-orange-800'
    };
    return colorMap[role] || 'bg-gray-100 text-gray-800';
  };

  const formatDuration = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const formatAddress = (address: string) => {
    return `${address.substring(0, 6)}...${address.substring(address.length - 4)}`;
  };

  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleDateString('zh-CN');
  };

  const toggleFavorite = (workId: string) => {
    setFavorites(prev => {
      const newFavorites = new Set(prev);
      if (newFavorites.has(workId)) {
        newFavorites.delete(workId);
      } else {
        newFavorites.add(workId);
      }
      return newFavorites;
    });
  };

  const togglePlay = (workId: string) => {
    if (playingWork === workId) {
      setPlayingWork(null);
    } else {
      setPlayingWork(workId);
    }
  };

  if (!wallet?.isConnected) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">请先连接钱包以查看音乐作品NFT</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
        <p className="mt-2 text-gray-500">加载中...</p>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">音乐作品NFT展示</h1>
        <p className="text-gray-600">查看已上链的音乐作品及其NFT信息、参与者贡献和交易历史</p>
        
        {/* 核心功能说明 */}
        <div className="mt-6 grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h3 className="font-semibold text-blue-800 mb-2">🎵 作品展示</h3>
            <p className="text-sm text-blue-700">展示已上链音乐作品的详细信息和NFT数据</p>
          </div>
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <h3 className="font-semibold text-green-800 mb-2">👥 贡献追踪</h3>
            <p className="text-sm text-green-700">清晰展示每个参与者的角色和贡献比例</p>
          </div>
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
            <h3 className="font-semibold text-purple-800 mb-2">💰 收益分成</h3>
            <p className="text-sm text-purple-700">透明展示收益分配机制和实时收益</p>
          </div>
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
            <h3 className="font-semibold text-orange-800 mb-2">📊 交易历史</h3>
            <p className="text-sm text-orange-700">完整记录NFT交易和持有者变更</p>
          </div>
        </div>
      </div>

      {/* 作品列表 */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {musicWorks.map((work) => (
          <div key={work.id} className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow">
            <div className="relative">
              <img 
                src={work.coverImage} 
                alt={work.title}
                className="w-full h-48 object-cover"
                onError={(e) => {
                  (e.target as HTMLImageElement).src = '/images/music-placeholder.jpg';
                }}
              />
              <div className="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
                <button
                  onClick={() => togglePlay(work.id)}
                  className="bg-white rounded-full p-3 hover:bg-gray-100 transition-colors"
                >
                  {playingWork === work.id ? (
                    <PauseIcon className="w-6 h-6 text-gray-800" />
                  ) : (
                    <PlayIcon className="w-6 h-6 text-gray-800" />
                  )}
                </button>
              </div>
              <button
                onClick={() => toggleFavorite(work.id)}
                className="absolute top-2 right-2 p-2 bg-white/80 rounded-full hover:bg-white"
              >
                {favorites.has(work.id) ? (
                  <HeartSolidIcon className="w-5 h-5 text-red-500" />
                ) : (
                  <HeartIcon className="w-5 h-5 text-gray-600" />
                )}
              </button>
              <div className="absolute top-2 left-2">
                <span className="px-2 py-1 text-xs bg-blue-600 text-white rounded-full">
                  {work.isOnChain ? '已上链' : '未上链'}
                </span>
              </div>
            </div>
            
            <div className="p-4">
              <h3 className="font-semibold text-lg mb-2">{work.title}</h3>
              <p className="text-gray-600 text-sm mb-3 line-clamp-2">{work.description}</p>
              
              <div className="flex items-center justify-between mb-3 text-sm text-gray-500">
                <div className="flex items-center gap-1">
                  <ClockIcon className="w-4 h-4" />
                  {formatDuration(work.duration)}
                </div>
                <span className="px-2 py-1 bg-gray-100 rounded text-xs">{work.genre}</span>
              </div>
              
              <div className="grid grid-cols-3 gap-4 mb-4 text-center">
                <div>
                  <p className="text-lg font-semibold text-blue-600">{work.floorPrice} SOL</p>
                  <p className="text-xs text-gray-500">地板价</p>
                </div>
                <div>
                  <p className="text-lg font-semibold text-green-600">{work.totalVolume} SOL</p>
                  <p className="text-xs text-gray-500">总交易量</p>
                </div>
                <div>
                  <p className="text-lg font-semibold text-purple-600">{work.holders}</p>
                  <p className="text-xs text-gray-500">持有者</p>
                </div>
              </div>
              
              <div className="flex gap-2">
                <button
                  onClick={() => setSelectedWork(work)}
                  className="flex-1 bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 flex items-center justify-center gap-2"
                >
                  <EyeIcon className="w-4 h-4" />
                  查看详情
                </button>
                <button className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200">
                  <ShareIcon className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* 详情模态框 */}
      {selectedWork && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold">{selectedWork.title}</h2>
                <button
                  onClick={() => setSelectedWork(null)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  ✕
                </button>
              </div>
            </div>
            
            <div className="p-6">
              {/* 标签页导航 */}
              <div className="flex border-b mb-6">
                {[
                  { key: 'overview', label: '概览', icon: MusicalNoteIcon },
                  { key: 'participants', label: '参与者', icon: UsersIcon },
                  { key: 'revenue', label: '收益分成', icon: CurrencyDollarIcon },
                  { key: 'transactions', label: '交易历史', icon: ChartBarIcon }
                ].map((tab) => (
                  <button
                    key={tab.key}
                    onClick={() => setActiveTab(tab.key as any)}
                    className={`py-2 px-4 border-b-2 font-medium text-sm flex items-center gap-2 ${
                      activeTab === tab.key
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    <tab.icon className="w-4 h-4" />
                    {tab.label}
                  </button>
                ))}
              </div>

              {/* 标签页内容 */}
              {activeTab === 'overview' && (
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <img 
                        src={selectedWork.coverImage} 
                        alt={selectedWork.title}
                        className="w-full h-64 object-cover rounded-lg"
                        onError={(e) => {
                          (e.target as HTMLImageElement).src = '/images/music-placeholder.jpg';
                        }}
                      />
                    </div>
                    <div className="space-y-4">
                      <div>
                        <h3 className="font-semibold mb-2">基本信息</h3>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-gray-500">时长:</span>
                            <span>{formatDuration(selectedWork.duration)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-500">类型:</span>
                            <span>{selectedWork.genre}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-500">创建时间:</span>
                            <span>{formatDate(selectedWork.createdAt)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-500">合约地址:</span>
                            <span className="font-mono">{formatAddress(selectedWork.contractAddress || '')}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'participants' && (
                <div className="space-y-4">
                  <h3 className="font-semibold">参与者贡献追踪</h3>
                  <div className="grid gap-4">
                    {selectedWork.participants.map((participant) => (
                      <div key={participant.address} className="border rounded-lg p-4">
                        <div className="flex items-center gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <h4 className="font-medium">{participant.name}</h4>
                              <span className={`px-2 py-1 text-xs rounded-full ${getRoleColor(participant.role)}`}>
                                {getRoleDisplayName(participant.role)}
                              </span>
                            </div>
                            <p className="text-sm text-gray-500">{formatAddress(participant.address)}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-lg font-semibold text-blue-600">{participant.contributionPercentage}%</p>
                            <p className="text-sm text-gray-500">贡献比例</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {activeTab === 'revenue' && (
                <div className="space-y-4">
                  <h3 className="font-semibold">版权分成明细</h3>
                  <div className="grid gap-4">
                    {selectedWork.revenueShares.map((share) => (
                      <div key={share.participantAddress} className="border rounded-lg p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <h4 className="font-medium">{share.participantName}</h4>
                            <span className={`inline-block px-2 py-1 text-xs rounded-full ${getRoleColor(share.role)}`}>
                              {getRoleDisplayName(share.role)}
                            </span>
                          </div>
                          <div className="text-right">
                            <p className="text-lg font-semibold text-green-600">{share.totalEarned} SOL</p>
                            <p className="text-sm text-gray-500">累计收益</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {activeTab === 'transactions' && (
                <div className="space-y-4">
                  <h3 className="font-semibold">NFT交易历史</h3>
                  <div className="space-y-3">
                    {selectedWork.transactions.map((transaction) => (
                      <div key={transaction.id} className="border rounded-lg p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <span className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full">
                              铸造
                            </span>
                            <span className="text-sm text-gray-500">{formatDate(transaction.timestamp)}</span>
                          </div>
                          <div className="text-right">
                            <p className="font-semibold">{transaction.price || 0} {transaction.currency}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-gray-600 mt-2">
                          <span>{formatAddress(transaction.from)}</span>
                          <ArrowRightIcon className="w-4 h-4" />
                          <span>{formatAddress(transaction.to)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MusicWorkNFTDisplay;
